package pl.michal_cyran.brand_hub.subpages.tutoring.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.gridTemplateColumns
import com.varabyte.kobweb.silk.components.icons.fa.FaAndroid
import com.varabyte.kobweb.silk.components.icons.fa.FaBell
import com.varabyte.kobweb.silk.components.icons.fa.FaBook
import com.varabyte.kobweb.silk.components.icons.fa.FaCalendarDays
import com.varabyte.kobweb.silk.components.icons.fa.FaCalendarMinus
import com.varabyte.kobweb.silk.components.icons.fa.FaComment
import com.varabyte.kobweb.silk.components.icons.fa.FaDumbbell
import com.varabyte.kobweb.silk.components.icons.fa.FaLocationPin
import com.varabyte.kobweb.silk.components.icons.fa.FaPencil
import com.varabyte.kobweb.silk.components.icons.fa.FaPerson
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.dom.H2 // Zmiana H1 na H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import pl.michal_cyran.brand_hub.subpages.tutoring.composables.PropertyCard

@Composable
fun TutoringLessons(
    modifier: Modifier = Modifier
) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(1.cssRem)
    ) {
        H2 {
            Text(
                "Standard nauczania matematyki i informatyki",
            )
        }

        P {
            Text(
                "Wykorzystuję autorskie narzędzia, nowoczesną technologię oraz w pełni indywidualne podejście, aby każda lekcja była maksymalnie efektywna."
            )
        }

        SimpleGrid(
            numColumns(base = 1, md = 3),
            modifier = FirstGridStyle.toModifier()
                .gap(1.cssRem)
                .then(modifier)
        ) {
            PropertyCard(
                icon = { size -> FaLocationPin(size = size) },
                title = "Online lub stacjonarnie",
                description = "Zajęcia prowadzę online lub stacjonarnie w Krakowie – na terenie dzielnic Ruczaj, Pychowice oraz w okolicach kampusu UJ.",
                index = 0,
            )
            PropertyCard(
                icon = { size -> FaBook(size = size) },
                title = "Materiały naukowe",
                description = "Dostępne dedykowane materiały PDF, dzięki którym sprawnie opanujesz teorię, wzory oraz schematy rozwiązywania zadań maturalnych.",
                index = 1,
            )
            PropertyCard(
                icon = { size -> FaCalendarMinus(size = size) },
                title = "Elastyczne odwoływanie lekcji",
                description = "Jeśli dasz mi znać dzień wcześniej, że nie dasz rady pojawić się na lekcji, nie będzie żadnego problemu. W nagłych przypadkach (np. choroba) możesz odwołać lekcję nawet kilka godzin wcześniej.",
                index = 2,
            )
        }
        SimpleGrid(
            numColumns(base = 1, md = 3),
            modifier = SecondGridStyle.toModifier()
                .gap(1.cssRem)
                .then(modifier)
        ) {
            PropertyCard(
                icon = { size -> FaBell(size = size) },
                title = "Automatyczne przypomnienia",
                description = "Mój autorski skrypt codziennie wieczorem przypomina o zajęciach na następny dzień. Dzięki temu nie zapomnisz o lekcji lub jej ewentualnym przełożeniu.",
                index = 0,
            )
            PropertyCard(
                icon = { size -> FaAndroid(size = size) },
                title = "Dedykowana aplikacja i strona",
                description = "Jestem autorem nowoczesnej platformy internetowej oraz aplikacji mobilnej ułatwiającej naukę matematyki. Moi uczniowie otrzymują do nich wczesny dostęp.",
                featured = true,
                index = 1,
            )
            PropertyCard(
                icon = { size -> FaCalendarDays(size = size) },
                title = "Grafik online",
                description = "Mój aktualny grafik zajęć jest dostępny dla każdego na serwerze Discord. Możesz wygodnie planować i rezerwować dogodne dla siebie terminy.",
                index = 2,
            )
        }
        SimpleGrid(
            numColumns(base = 1, md = 4),
            modifier = Modifier
                .gap(1.cssRem)
                .then(modifier)
        ) {
            PropertyCard(
                icon = { size -> FaPerson(size = size) },
                title = "Indywidualne podejście",
                description = "Tempo pracy i poziom trudności zadań dostosowuję bezpośrednio do Twoich celów, poziomu wiedzy oraz programu szkolnego.",
                index = 0,
            )
            PropertyCard(
                icon = { size -> FaComment(size = size) },
                title = "Wsparcie poza lekcjami",
                description = "Masz problem z trudnym zadaniem domowym lub przygotowaniem do klasówki? Możesz napisać do mnie na Discordzie, a chętnie pomogę Ci również poza godzinami zajęć.",
                index = 1,
            )
            PropertyCard(
                icon = { size -> FaDumbbell(size = size) },
                title = "Zadania domowe",
                description = "Po każdych zajęciach przygotowuję zestaw zadań lub próbny arkusz egzaminacyjny do samodzielnego wykonania, który następnie wspólnie szczegółowo omawiamy.",
                index = 2,
            )
            PropertyCard(
                icon = { size -> FaPencil(size = size) },
                title = "Dodatkowe materiały",
                description = "Jeśli potrzebujesz dodatkowych ćwiczeń, teorii, tablic matematycznych lub wyjaśnienia konkretnego działu – przygotuję spersonalizowany zestaw materiałów.",
                index = 3,
            )
        }
    }
}

val FirstGridStyle = CssStyle {
    Breakpoint.MD {
        Modifier
            .gridTemplateColumns {
                size(1.fr)
                size(1.fr)
                size(2.fr)
            }
    }
}

val SecondGridStyle = CssStyle {
    Breakpoint.MD {
        Modifier
            .gridTemplateColumns {
                size(1.fr)
                size(2.fr)
                size(1.fr)
            }
    }
}